<template>
    <ag-grid-vue
        class="w-full h-full min-w-[830px] ag-theme-balham"
        :column-defs="columns"
        :row-data="transactionsWithLines"
        :grid-options="gridOptions"
        :theme="agTheme"
    />
</template>

<script setup lang="ts">

import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {computed, inject, nextTick, onUnmounted, Ref, shallowRef} from 'vue'
import {
    CellClassParams,
    ColDef,
    ColSpanParams,
    EditableCallbackParams,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    ITooltipParams,
    ValueGetterParams,
    ValueSetterParams
} from 'ag-grid-community'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import {Draft} from '@/draft/drafts.service'
import DraftListAccountCell from '@/draft/DraftListAccountCell.vue'
import DraftsEntryActions from '@/draft/DraftsEntryActions.vue'
import DraftNameCell from '@/draft/DraftNameCell.vue'
import {useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import type {GetRowIdParams} from 'ag-grid-community/dist/types/src/interfaces/iCallbackParams'
import {globalDefaultColDef} from '@/agUtils'
import {Transaction} from '@/types/transaction/Transaction'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {useVatService} from '@/vat/vat.service'
import DraftTransactionNameCell from '@/draft/DraftTransactionNameCell.vue'
import {useTransactionDrafts} from '@/transaction-journal/transaction.draft.service'
import {TRANSACTION_DRAFT_DELETED, TRANSACTION_DRAFT_LOADED, TRANSACTION_DRAFT_UPDATED} from '@/types/transaction/TransactionEvent'
import {useAttachDocumentService} from '@/transaction/create-transaction/AttachDocument.service'
import {notifyError, notifySuccess} from '@/services/infrastructure/notification.service'
import {transactionLinesToEntries} from '@/transaction/transaction.mapper'
import {useEditTransactionCreator} from '@/transaction/edit-transaction/EditTransaction.service'
import {getTransactionByUuid} from '@/transaction/transaction.service'
import {v4 as uuidv4} from 'uuid'
const listId = uuidv4()
const {cleanUp} = useEmitter()
const {getVatOptionById} = useVatService()
const {transactionEntryLines} = useTransactionCreator()
const {existingTransaction} = useEditTransactionCreator()
const {drafts, loading, updateDraft, deleteDraft, skipAutoSaveDraft} = useTransactionDrafts()
const {copyAndAttachUploadedDocumentsToTransaction} = useAttachDocumentService()

const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')

const gridApi = shallowRef<GridApi>()

enum RowType {'T', 'TL', 'D'}

type TransactionJournalGridLine = (Transaction | TransactionLine) & { type: RowType }

const transactionsWithLines = computed(() => {
    const extended = drafts.value.map(draftTransaction => {
        return {
            ...draftTransaction,
            domId: `draft_trans_${listId}_${draftTransaction.uuid}`,
            type: RowType.T
        }
    })

    const rows = []
    for (const draftTransaction of extended) {
        rows.push(draftTransaction)
        for (let i = 0; i < draftTransaction.content.entryLines.length; i++) {
            const tl = {
                ...draftTransaction.content.entryLines[i],
                type: RowType.TL,
                transactionName: ''
            }

            rows.push(tl)
        }
        const detailsLine = {
            editTransactionUuid: draftTransaction.editTransactionUuid,
            uuid: draftTransaction.uuid + '_details',
            type: RowType.D,
        }
        rows.push(detailsLine)
    }

    return rows
})

const columns: ColDef[] = [
    {
        headerName: 'Kladd',
        colId: 'name',
        editable: (params: EditableCallbackParams) => {
            return params.data.type === RowType.T
        },
        width: 180,
        valueSetter: (params: ValueSetterParams) => {
            if (params.data.type !== RowType.T) return false

            updateDraft({...params.data, name: params.newValue}).then(res => {
                if (res.isRight()) {
                    emitter.emit(TRANSACTION_DRAFT_UPDATED)
                    return true
                } else {
                    notifyError({title: 'Feilet', description: `Kladden ${params.data.name} kunne ikke oppdateres.`})
                }
            })

            return false
        },
        cellClass: (params: CellClassParams) => {
            return params.data.type === RowType.T ? 'cursor-pointer' : ''
        },
        field: 'name',
        tooltipValueGetter: (p: ITooltipParams) => p.data.name,
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.T) {
                return {component: DraftNameCell}
            }
        },
    },
    {
        headerName: 'Bilagsnr.',
        colId: 'bilagsnr',
        width: 115,
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.T) {
                return {component: DraftTransactionNameCell}
            } else if (data.type === RowType.TL) {
                return {component: () => formattedDate(data.date)}
            }
        },
    },
    {
        flex: 1,
        colId: 'description',
        headerName: 'Beskrivelse',
        cellRenderer: ({data}: ICellRendererParams) => data.description,
        colSpan: ({data}: ColSpanParams) => {
            if (data.type === RowType.T) return 4
            if (data.type === RowType.D) return 6
            return 1
        },
        cellClass: ({data}: CellClassParams) => {
            if (data.type === RowType.T) return 'text-[16px] mt-[5px]'
        }
    },
    {
        flex: 1,
        colId: 'kontoKundeLev',
        headerName: 'Konto / kunde / lev.',
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.TL) {
                return {component: DraftListAccountCell}
            }
        },
    },
    {
        headerName: 'MVA',
        width: 60,
        colId: 'vat',
        field: 'vatOptionId',
        valueGetter: ({data}: ValueGetterParams) => {
            return getVatOptionById(data.vatOptionId)?.code || 0
        },
        cellClassRules: {
            'opacity-20': (params: CellClassParams) => params.value === 0
        }
    },
    {
        headerName: 'Prosjekt',
        width: 100,
        colId: 'project',
        field: 'project',
    },
    {
        headerName: 'Avdeling',
        width: 100,
        colId: 'department',
        field: 'department',
    },
    {
        width: 90,
        headerName: 'Beløp',
        colId: 'amount',
        headerClass: 'header-right',
        cellClass: 'text-right',
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.type === RowType.T) {
                return {component: DraftsEntryActions}
            } else if (data.type === RowType.TL) {
                return {component: () => formatCurrency(data.amount)}
            }
        },
        cellRendererParams: (params: ICellRendererParams) => {
            const draft = params.data
            return {
                deleteSingleDraft: async () => {
                    await deleteDraft(draft.uuid)
                    notifySuccess({title: 'Slettet', description: `Kladden ${draft.name} ble slettet.`})
                    emitter.emit(TRANSACTION_DRAFT_DELETED)
                },
                applyDraftToTransactionLine: () => {
                    skipAutoSaveDraft.value = true
                    nextTick(async () => {
                        // TODO handle somewhat differently, think bigger picture
                        if (draft.editTransactionUuid) {
                            existingTransaction.value = await getTransactionByUuid(draft.editTransactionUuid)
                        }
                        copyAndAttachUploadedDocumentsToTransaction(draft.content.documentsMetas)
                        transactionEntryLines.value = transactionLinesToEntries(draft?.content.entryLines)
                        notifySuccess({title: 'Suksess', description: `Kladden ${draft?.name} ble hentet.`})
                        nextTick(() => skipAutoSaveDraft.value = false)
                        emitter.emit(TRANSACTION_DRAFT_LOADED, draft.uuid)
                    })
                }
            }
        }
    },
]

const gridOptions: GridOptions = {
    getRowHeight: ({data}: { data: TransactionJournalGridLine }) => {
        if (data.type === RowType.TL) {
            return 30
        } else {
            return 45
        }
    },
    defaultColDef: {
        ...globalDefaultColDef,
        resizable: true,
        sortable: false,
    },
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => loading.value ? 'Laster inn kladd....' : 'Ingen kladd'
    },
    getRowId: (params: GetRowIdParams<Draft>) => params.data.uuid ?? '',
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    context: {
        panelId,
        panelPathSegment
    }
}

onUnmounted(cleanUp)
</script>

<style>
</style>